import { useCreateTeamRequestMutation } from 'features/TeamChat/api/teamChat.api'
import { teamErrorText } from 'features/TeamChat/api/teamChat.transform'
import type { TeamMember } from 'features/TeamChat/api/teamChat.types'
import { REQUEST_MAX_LENGTH, TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { MentionInput } from '../MentionInput'
import { TeamButton } from '../TeamButton'
import { useTeamDrafts } from '../TeamDrafts'
import type { RequestDraft } from '../TeamDrafts'

type Props = { members: TeamMember[]; onClose: () => void }

const FIELD_ID = 'team-request-title'

export const RequestComposer = ({ members, onClose }: Props) => {
  const { request, setRequest, requestSending } = useTeamDrafts()
  const { title, error, isSending } = request
  const [createRequest] = useCreateTeamRequestMutation()
  const update = (patch: Partial<RequestDraft>) => setRequest((s) => ({ ...s, ...patch }))

  const submit = async () => {
    const trimmed = title.trim()
    if (requestSending.current) return
    if (!trimmed) return update({ error: TEAM_COPY.REQUEST_REQUIRED })
    requestSending.current = true
    update({ isSending: true })
    try {
      await createRequest({ title: trimmed }).unwrap()
      onClose()
    } catch (e) {
      update({ error: teamErrorText(e, TEAM_COPY.SEND_FAILED) })
    } finally {
      requestSending.current = false
      update({ isSending: false })
    }
  }

  return (
    <div
      className={`${styles.footer} ${styles.footerTinted} ${styles.slideUp}`}
      data-mention-anchor>
      <label htmlFor={FIELD_ID} className={styles.fieldLabel}>
        {TEAM_COPY.WHAT_DO_YOU_NEED} <span className={styles.fieldHint}>{TEAM_COPY.USE_AT}</span>
      </label>
      <MentionInput
        variant='field'
        id={FIELD_ID}
        ariaLabel={TEAM_COPY.WHAT_DO_YOU_NEED}
        value={title}
        members={members}
        maxLength={REQUEST_MAX_LENGTH}
        placeholder={TEAM_COPY.REQUEST_PLACEHOLDER}
        onChange={(v) => update({ title: v, error: '' })}
        onSubmit={submit}
      />
      <div role='alert' className={styles.error}>
        {error}
      </div>
      <div className={styles.row}>
        <TeamButton grow loading={isSending} disabled={isSending} onClick={submit}>
          {TEAM_COPY.SEND_TO_TEAM}
        </TeamButton>
        <TeamButton variant='secondary' disabled={isSending} onClick={onClose}>
          {TEAM_COPY.CANCEL}
        </TeamButton>
      </div>
    </div>
  )
}
